import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { VendorAuthProvider, useVendorAuth } from "./context/VendorAuthContext";
import LandingPage from "./pages/LandingPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import MasterList from "./pages/admin/MasterList";
import Apartments from "./pages/admin/Apartments";
import Vendors from "./pages/admin/Vendors";
import VendorDetails from "./pages/admin/VendorDetails";
import Settings from "./pages/admin/Settings";
import Banner from "./pages/admin/Banner";
import Interstitial from "./pages/admin/Interstitial";
import Customers from "./pages/admin/Customers";
import AdminOrders from "./pages/admin/AdminOrders";
import Leads from "./pages/admin/Leads";
import VendorLogin from "./pages/vendor/VendorLogin";
import VendorLayout from "./pages/vendor/VendorLayout";
import VendorDashboard from "./pages/vendor/VendorDashboard";
import VendorProducts from "./pages/vendor/VendorProducts";
import VendorOrders from "./pages/vendor/VendorOrders";
import VendorOrderDetail from "./pages/vendor/VendorOrderDetail";
import VendorCustomers from "./pages/vendor/VendorCustomers";
import VendorPickupPoints from "./pages/vendor/VendorPickupPoints";
import VendorProfile from "./pages/vendor/VendorProfile";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Admin Login Route - redirect if already logged in
const AdminLoginRoute = () => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (user && isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <AdminLogin />;
};

// Vendor Protected Route
const VendorProtectedRoute = ({ children }) => {
  const { vendor, loading } = useVendorAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!vendor) {
    return <Navigate to="/vendor/login" replace />;
  }

  return children;
};

const VendorLoginRoute = () => {
  const { vendor, loading } = useVendorAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (vendor) {
    return <Navigate to="/vendor/dashboard" replace />;
  }

  return <VendorLogin />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* 404 — catch-all */}
      <Route path="*" element={<NotFound />} />
      
      {/* Admin Login */}
      <Route path="/admin/login" element={<AdminLoginRoute />} />
      
      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="masters/:type" element={<MasterList />} />
        <Route path="apartments" element={<Apartments />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="vendors/:id" element={<VendorDetails />} />
        <Route path="customers" element={<Customers />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="leads" element={<Leads />} />
        <Route path="ads/banner" element={<Banner />} />
        <Route path="ads/interstitial" element={<Interstitial />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Vendor Login */}
      <Route path="/vendor/login" element={<VendorLoginRoute />} />

      {/* Protected Vendor Routes */}
      <Route
        path="/vendor"
        element={
          <VendorProtectedRoute>
            <VendorLayout />
          </VendorProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/vendor/dashboard" replace />} />
        <Route path="dashboard" element={<VendorDashboard />} />
        <Route path="products" element={<VendorProducts />} />
        <Route path="orders" element={<VendorOrders />} />
        <Route path="orders/:orderId" element={<VendorOrderDetail />} />
        <Route path="customers" element={<VendorCustomers />} />
        <Route path="pickup-points" element={<VendorPickupPoints />} />
        <Route path="profile" element={<VendorProfile />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <VendorAuthProvider>
            <AppRoutes />
          </VendorAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
